/**
 * Valida e formata um CNPJ (incluindo o novo formato com letras a partir de 2026).
 * @param cnpj CNPJ a ser validado.
 * @param format Retorna o CNPJ formatado caso seja válido, se `true`.
 * @returns O CNPJ formatado se for válido e `format` for `true`, ou os mesmos dados de entrada se for inválido.
 */
export const validaCNPJ = (cnpj: string, format: boolean = false): string => {
  // Remover caracteres não alfanuméricos
  const cleaned = cnpj.replace(/[^a-zA-Z0-9]/g, '');

  // O novo formato aceita letras, mas mantém o comprimento de 14 caracteres
  if (cleaned.length !== 14) return cnpj;

  // Verifica se todos os caracteres são iguais (ex: "AAAAAAAAAAAA" ou "11111111111111")
  if (/^([a-zA-Z0-9])\1{13}$/.test(cleaned)) return cnpj;

  // Separar base (12 primeiros) e dígitos verificadores
  const base = cleaned.slice(0, 12);
  const dv = cleaned.slice(12);

  // Calcula os dígitos verificadores
  const calculaDV = (base: string): string => {
    const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    // Função para calcular um dígito com base nos pesos
    const calcularDigito = (base: string, pesos: number[]): number => {
      const soma = base
        .split('')
        .map((char, i) => {
          const valor = /[a-zA-Z]/.test(char) // Se for letra, usa seu valor ASCII ajustado
            ? char.toUpperCase().charCodeAt(0) - 55 // A = 10, B = 11, ..., Z = 35
            : parseInt(char, 10); // Se for número, converte diretamente
          return valor * pesos[i];
        })
        .reduce((acc, curr) => acc + curr, 0);

      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    // Calcula o primeiro e segundo dígitos verificadores
    const dv1 = calcularDigito(base, pesos1);
    const dv2 = calcularDigito(base + dv1, pesos2);

    return `${dv1}${dv2}`;
  };

  // Verifica se os dígitos verificadores calculados batem com os fornecidos
  const isValid = calculaDV(base) === dv;

  if (!isValid) return cnpj;

  // Retorna o CNPJ formatado, se solicitado
  if (format) {
    return cleaned.replace(
      /^(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})$/,
      '$1.$2.$3/$4-$5',
    );
  }

  return cnpj;
};
